export type PortalUser = {
  id: string;
  email: string;
  display_name: string;
  role: "internal-admin" | "customer-admin";
  customer_id?: string;
  customer_name?: string;
  commercial_mode?: "shadow" | "formal";
};

export type Summary = {
  characters_this_month: number;
  seconds_this_month: number;
  amount_this_month: string;
  successful_requests: number;
  failure_rate: number;
  daily_usage: Array<{ date: string; characters: number; seconds: number; amount: number }>;
  recent_usage: UsageEvent[];
};

export type Extension = {
  name: string;
  type: string;
  required?: boolean;
};

export type Model = {
  id: string;
  label: string;
  kind: "tts" | "vc" | "asr" | "separation";
  adapter: string;
  config_version: string;
  output_formats: string[];
  extensions: Extension[];
};

export type UsageEvent = {
  id: string;
  request_id: string;
  completed_at: string;
  customer_name?: string;
  client_name?: string;
  key_id?: string;
  key_prefix?: string;
  channel: "internal" | "external-free" | "external-paid";
  model_id: string;
  modality: "tts" | "vc" | "asr" | "separation";
  outcome: "success" | "failed" | "cancelled";
  billable_characters: number;
  billable_seconds: number;
  billable_duration_ms: number;
  amount?: string;
  shadow: boolean;
};

export type Customer = {
  id: string;
  code: string;
  name: string;
  status: string;
  billing_enabled_at?: string;
  admin_email?: string;
  admin_display_name?: string;
  grants: string[];
};

export type APIKey = {
  id: string;
  client_id: string;
  prefix: string;
  status: string;
  expires_at?: string;
  last_used_at?: string;
  created_at: string;
  recoverable: boolean;
};

export type APIClient = {
  id: string;
  customer_id?: string;
  customer_name?: string;
  name: string;
  channel: string;
  status: string;
  grants: string[];
  keys: APIKey[];
};

export type Invoice = {
  id: string;
  customer_name: string;
  period_start: string;
  period_end: string;
  total_amount: string;
  paid_amount: string;
  status: string;
  shadow: boolean;
};

export type PriceVersion = {
	 id: string;
	 customer_id: string;
	 customer_name: string;
	 modality: "tts" | "vc" | "separation";
	 unit_price: string;
	 effective_from: string;
	 effective_until?: string;
	 shadow: boolean;
};

export type PortalAccount = {
	 id: string;
	 customer_id?: string;
	 customer_name?: string;
	 email: string;
	 display_name: string;
	 role: string;
	 status: string;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "请求失败" }));
    throw new Error(body.error ?? `请求失败 (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  me: () => request<PortalUser>("/api/portal/me"),
  login: (email: string, password: string) =>
    request<PortalUser>("/api/portal/sessions", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: boolean }>("/api/portal/sessions/current", { method: "DELETE" }),
  updateProfile: (input: { email: string; display_name: string; current_password: string }) =>
    request<PortalUser>("/api/portal/profile", { method: "PATCH", body: JSON.stringify(input) }),
  changePassword: (input: { current_password: string; new_password: string }) =>
    request<{ ok: boolean }>("/api/portal/profile/password", { method: "POST", body: JSON.stringify(input) }),
  summary: () => request<Summary>("/api/control/summary"),
  models: () => request<{ data: Model[] }>("/api/control/models").then((result) => result.data),
  usage: () => request<{ data: UsageEvent[] }>("/api/control/usage?limit=100").then((result) => result.data),
  customers: () => request<{ data: Customer[] }>("/api/control/customers").then((result) => result.data),
  clients: () => request<{ data: APIClient[] }>("/api/control/clients").then((result) => result.data),
  invoices: () => request<{ data: Invoice[] }>("/api/control/invoices").then((result) => result.data),
  prices: () => request<{ data: PriceVersion[] }>("/api/control/prices").then((result) => result.data),
  users: () => request<{ data: PortalAccount[] }>("/api/control/users").then((result) => result.data),
  createCustomer: (input: { code: string; name: string; billing_enabled_at?: string; admin_email: string; admin_display_name: string; admin_password: string; grants: string[] }) =>
    request<Customer>("/api/control/customers", { method: "POST", body: JSON.stringify(input) }),
  createClient: (input: { customer_id?: string; name: string; channel: string; grants: string[]; expires_at?: string }) =>
    request<{ client: APIClient; token: string }>("/api/control/clients", { method: "POST", body: JSON.stringify(input) }),
  revokeClient: (id: string) => request<{ ok: boolean }>(`/api/control/clients/${id}/revoke`, { method: "POST", body: "{}" }),
  issueAPIKey: (clientId: string, expires_at?: string) =>
    request<{ key: APIKey; token: string }>(`/api/control/clients/${clientId}/keys`, { method: "POST", body: JSON.stringify({ expires_at }) }),
  revealAPIKey: (id: string) => request<{ key_id: string; token: string }>(`/api/control/keys/${id}/secret`, { method: "POST", body: "{}" }),
  rotateAPIKey: (id: string, expires_at?: string) =>
    request<{ key: APIKey; token: string }>(`/api/control/keys/${id}/rotate`, { method: "POST", body: JSON.stringify({ expires_at }) }),
  revokeAPIKey: (id: string) => request<{ ok: boolean }>(`/api/control/keys/${id}/revoke`, { method: "POST", body: "{}" }),
  updateCustomerGrants: (id: string, grants: string[]) =>
    request<{ ok: boolean }>(`/api/control/customers/${id}/grants`, { method: "PUT", body: JSON.stringify({ grants }) }),
  createPrice: (input: { customer_id: string; modality: "tts" | "vc" | "separation"; unit_price: string; effective_from: string; effective_until?: string; shadow: boolean }) =>
    request<PriceVersion>("/api/control/prices", { method: "POST", body: JSON.stringify(input) }),
  generateInvoice: (input: { customer_id: string; period: string; shadow: boolean }) =>
    request<Invoice>("/api/control/invoices", { method: "POST", body: JSON.stringify(input) }),
};
